/**
 * Companion (media-owning desktop endpoint) helpers shared by the signalling surface (CTI)
 * and the companion itself. Model A: the CTI signals the companion over the backend (Home ->
 * TOP client WebSocket) to act on a call the companion already owns.
 */
import isElectron from 'is-electron';
import { isEmbeddedTypeCti, isTeamsAnyVersion } from 'flow/utils/app-utils';
import { getLocalCallSessions } from 'flow/selectors/call-selectors';
import {
    selectDesignatedCompanionDevice,
    selectHandoverToDesktop,
    selectUseAsCompanion,
} from 'flow/selectors/app-selectors';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import {
    STALE_COMPANION_DEVICE_MS,
    isCompanionModeEnabled,
    isLocalhost,
    isStaleCompanionDevice,
} from './companion-mode';
import { selectCompanionResponsive } from 'flow/selectors/companion-selectors';

export {
    isCompanionModeEnabled,
    isStaleCompanionDevice,
    STALE_COMPANION_DEVICE_MS,
};

/**
 * Whether this client should act on companion commands it receives.
 *
 * The feature switch alone is not enough. It is per *user*, so it is true in every client they log
 * into, while "Use as Companion" is the per-install answer to "should this machine own the media".
 * A desktop app the user has explicitly turned that off on must ignore commands rather than
 * answering calls and unmuting microphones on a machine they have deliberately opted out - and
 * because the push fans out to every registered desktop client, without this a user with two
 * installs would have both of them act.
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const isDesignatedCompanion = (state) =>
    isCompanionModeEnabled(state) && selectUseAsCompanion(state);

/**
 * Whether this client is the kind of surface that *could* delegate to a companion, ignoring whether
 * the user currently wants it to.
 *
 * Only the settings screen should use this: it decides whether to offer the "Use Companion for
 * calls" toggle, and that has to stay visible after the user turns delegation off - otherwise there
 * would be no way back on. Everything that acts on a call wants
 * {@link isCompanionSignallingSurface}, which folds the user's choice in.
 *
 * **Teams only in anything deployed.** The other CTI integrations (HubSpot, Salesforce, Zendesk,
 * Dynamics) render the same components and would otherwise inherit companion behaviour, which is not
 * in scope: the companion is being introduced for the Teams embedded app first. They stay exactly as
 * they are. Mobile embeds are excluded by construction - there is no companion on a phone.
 *
 * The generic CTI embed is additionally allowed **on localhost only**, purely so the chain can be
 * exercised without Teams. The Teams embed paths cannot boot in a plain browser: `isInTeams()` in
 * entry.js substring-matches `embed/teams` and awaits `microsoftTeams.app.initialize()`, which fails
 * with "No Parent window found" outside a Teams host. Without this exception local testing would
 * require tunnelling the webapp to a public https URL and a Teams manifest pointing at it. Scoped to
 * localhost it cannot affect a deployed client - the same guard the localStorage override uses.
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const isCompanionCapableSurface = (state) => {
    if (!isCompanionModeEnabled(state) || isElectron()) {
        return false;
    }
    const embedded = state?.app?.get('embedded');
    if (isTeamsAnyVersion(embedded)) {
        return true;
    }
    return isEmbeddedTypeCti(embedded) && (isLocalhost() || IS_UAE_HOST);
};

/**
 * Whether this client should delegate to the companion rather than act on the call itself.
 *
 * Structurally capable of it ({@link isCompanionCapableSurface}) *and* the user has left handover
 * on. A setting of its own rather than a call method: handover is not another answer to "which
 * device takes my calls", it is whether this client should be answering that question itself at
 * all. The distinction is load-bearing, because the call methods are what happens when no desktop
 * app is there - making the desktop app one of them would take the softphone away exactly when it
 * is needed. Keeping the choice here rather than at each call site is what stops dial, answer,
 * hold, mute and DTMF disagreeing about who owns the call.
 *
 * Distinct from {@link isCompanionModeEnabled}, and the distinction matters: the feature switch is
 * per *user*, so it is true in every client that user logs into - including the companion itself and
 * the ordinary web app. Only a signalling surface should delegate. Without that narrowing, a user
 * with the feature enabled would find their ordinary web app relaying dials and mutes to their
 * desktop app instead of using its own softphone - mute in particular would report success while
 * leaving the microphone live.
 *
 * The companion end of the same question is {@link isDesignatedCompanion}, not this: it is neither a
 * CTI nor a Teams embed, so it would fail this check and ignore every command it receives.
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const isCompanionSignallingSurface = (state) =>
    isCompanionCapableSurface(state) && selectHandoverToDesktop(state);

/**
 * Whether an in-call control pressed here has to be carried out by the companion instead.
 *
 * The single definition of the relay boundary, so the feature switch cannot come to mean one thing
 * for mute and a subtly different thing for DTMF. Both need the same two conditions: this client is
 * a signalling surface, *and* it holds no media of its own - a CTI that registered its softphone
 * anyway has a real session, and the control belongs in that call rather than on the wire.
 *
 * Note what this is not for: {@link isCompanionSignallingSurface} on its own still guards placing a
 * call, because at dial time there is no session to check and never can be. Keeping that case
 * visibly separate is the point - it is a deliberate difference, not an oversight.
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const shouldRelayToCompanion = (state) =>
    isCompanionSignallingSurface(state) &&
    getLocalCallSessions(state).isEmpty();

/**
 * Generate a one-time token so the companion can de-duplicate repeated deliveries of the same
 * command (the push may fan out to more than one desktop client / be redelivered).
 *
 * @returns {string}
 */
export const generateCompanionNonce = () => {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch {
        // fall through to the non-crypto fallback below
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * How long a registered desktop install stays trustworthy. Registration stamps `lastseen` at every
 * boot, so a device quiet for this long is one nobody has opened - shut down, retired, or an
 * install now logged into a different account, whose row on this account stopped being updated the
 * moment it was. Mirrors the IP-phone warning's tolerance.
 */
/**
 * Whether this surface should relay a call to the companion right now, as opposed to merely being
 * configured to.
 *
 * {@link isCompanionSignallingSurface} answers what the user chose; this adds whether there is
 * anything to relay *to*, and it is the difference between a call and a silence. A dial handed to
 * the companion does not come back - Home pushes it to the designated desktop and no ack returns -
 * so a surface that relays into an account with no designated device, or to one nobody has opened
 * in a fortnight, loses the call with nothing shown to the user. Refusing here drops the dial back
 * through to the softphone or Home paths, which do ring something.
 *
 * Only the outbound path needs this. Relaying an in-call command to a companion that is not there
 * fails harmlessly - the call is already up on some other device and the button simply does
 * nothing - whereas answering locally instead would move media to the wrong machine mid-call.
 *
 * @param {object} state redux state
 * @returns {boolean}
 */
export const canRelayCallToCompanion = (state) => {
    if (!isCompanionSignallingSurface(state)) {
        return false;
    }
    const designated = selectDesignatedCompanionDevice(state);
    return (
        !!designated &&
        !isStaleCompanionDevice(designated) &&
        selectCompanionResponsive(state) !== false
    );
};

export const selectCompanionAnswersCalls = (state) =>
    canRelayCallToCompanion(state) && selectCompanionResponsive(state) === true;
